import React, { useState, useEffect } from "react";
import Menu from "./Menu";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Pagination from "./components/Pagination";
import useApiUrls from "../useApiUrls";

// Custom icons component to replace FontAwesome
const Icon = ({ name }) => {
  const icons = {
    faTable: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z" />
      </svg>
    ),
    faChevronLeft: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
      </svg>
    ),
    faChevronRight: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
      </svg>
    ),
    faTimes: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
      </svg>
    ),
    faPlus: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
      </svg>
    ),
    faSpinner: (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="spin" viewBox="0 0 16 16">
        <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
      </svg>
    )
  };

  return icons[name] || null;
};

// Modern color scheme
const colors = {
  primary: 'var(--info)',      // Modern blue
  primaryHover: 'var(--info)',
  secondary: 'var(--text-muted)',    // Slate gray
  success: 'var(--success)',      // Emerald green
  successHover: 'var(--success)',
  warning: 'var(--warning)',      // Amber
  danger: 'var(--danger)',       // Red
  dangerHover: 'var(--danger)',
  info: 'var(--info)',         // Cyan
  infoHover: 'var(--info)',
  light: 'var(--surface2)',        // Very light gray
  lighter: 'var(--surface)',      // Light gray
  dark: 'var(--surface)',         // Dark slate
  border: 'var(--border)',       // Light border
  text: 'var(--text)',         // Text color
  textMuted: 'var(--text-muted)',    // Muted text
  background: 'var(--bg)'    // Page background
};

// CSS Styles with modern color scheme
const styles = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
    maxWidth: '100%',
    backgroundColor: colors.background,
    position: 'relative'
  },
  contentWrapper: {
    flexGrow: 1,
    padding: '20px',
    backgroundColor: colors.background,
    transition: 'margin-left 0.3s ease-in-out'
  },
  contentHeader: {
    padding: '15px 0'
  },
  containerFluid: {
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px',
    marginRight: 'auto',
    marginLeft: 'auto'
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: '-15px',
    marginLeft: '-15px'
  },
  col12: {
    flex: '0 0 100%',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px'
  },
  col6: {
    flex: '0 0 50%',
    maxWidth: '50%',
    position: 'relative',
    width: '100%',
    paddingRight: '15px',
    paddingLeft: '15px'
  },
  mb2: {
    marginBottom: '0.5rem'
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    wordWrap: 'break-word',
    backgroundColor: 'var(--surface)',
    backgroundClip: 'border-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    marginBottom: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  cardHeader: {
    padding: '1.25rem 1.5rem',
    marginBottom: 0,
    backgroundColor: colors.lighter,
    borderBottom: `1px solid ${colors.border}`,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  cardTitle: {
    float: 'left',
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
  },
  cardBody: {
    flex: '1 1 auto',
    padding: '1.5rem'
  },
  cardFooter: {
    padding: '1rem 1.5rem',
    backgroundColor: colors.lighter,
    borderTop: `1px solid ${colors.border}`,
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px'
  },
  table: {
    width: '100%',
    marginBottom: '1rem',
    color: colors.text,
    borderCollapse: 'collapse'
  },
  tableBordered: {
    border: `1px solid ${colors.border}`
  },
  tableHover: {
    cursor: 'pointer'
  },
  th: {
    padding: '1rem 0.75rem',
    verticalAlign: 'top',
    borderTop: `1px solid ${colors.border}`,
    borderBottom: `2px solid ${colors.border}`,
    backgroundColor: colors.lighter,
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  td: {
    padding: '0.875rem 0.75rem',
    verticalAlign: 'top',
    borderTop: `1px solid ${colors.border}`,
    color: colors.text
  },
  textCenter: {
    textAlign: 'center'
  },
  badge: {
    display: 'inline-block',
    padding: '0.375em 0.75em',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'baseline',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  },
  badgeSuccess: {
    color: '#fff',
    backgroundColor: colors.success
  },
  badgeDanger: {
    color: '#fff',
    backgroundColor: colors.danger
  },
  btn: {
    display: 'inline-block',
    fontWeight: 500,
    textAlign: 'center',
    verticalAlign: 'middle',
    userSelect: 'none',
    border: '1px solid transparent',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    borderRadius: '8px',
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  btnSm: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    borderRadius: '6px'
  },
  btnGroup: {
    position: 'relative',
    display: 'inline-flex',
    verticalAlign: 'middle',
    gap: '0.5rem'
  },
  btnInfo: {
    color: '#fff',
    backgroundColor: colors.info,
    borderColor: colors.info
  },
  btnInfoHover: {
    backgroundColor: colors.infoHover,
    borderColor: colors.infoHover
  },
  btnPrimary: {
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  btnPrimaryHover: {
    backgroundColor: colors.primaryHover,
    borderColor: colors.primaryHover
  },
  btnSuccess: {
    color: '#fff',
    backgroundColor: colors.success,
    borderColor: colors.success
  },
  btnSuccessHover: {
    backgroundColor: colors.successHover,
    borderColor: colors.successHover
  },
  btnDefault: {
    color: colors.text,
    backgroundColor: 'var(--surface)',
    borderColor: colors.border
  },
  btnDefaultHover: {
    backgroundColor: colors.lighter,
    borderColor: colors.secondary
  },
  btnDanger: {
    color: '#fff',
    backgroundColor: colors.danger,
    borderColor: colors.danger
  },
  btnDangerHover: {
    backgroundColor: colors.dangerHover,
    borderColor: colors.dangerHover
  },
  ml1: {
    marginLeft: '0.25rem'
  },
  ml2: {
    marginLeft: '0.5rem'
  },
  mr1: {
    marginRight: '0.25rem'
  },
  mr2: {
    marginRight: '0.5rem'
  },
  mt1: {
    marginTop: '0.25rem'
  },
  mt2: {
    marginTop: '0.5rem'
  },
  p4: {
    padding: '1.5rem'
  },
  formControl: {
    display: 'block',
    width: '100%',
    height: 'calc(1.5em + 1rem + 2px)',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: colors.text,
    backgroundColor: 'var(--surface)',
    backgroundClip: 'padding-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out'
  },
  formControlSm: {
    height: 'calc(1.5em + 0.5rem + 2px)',
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    borderRadius: '6px'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  dFlex: {
    display: 'flex'
  },
  justifyContentEnd: {
    justifyContent: 'flex-end'
  },
  alignItemsCenter: {
    alignItems: 'center'
  },
  flex1: {
    flex: 1
  },
  pagination: {
    display: 'flex',
    paddingLeft: 0,
    listStyle: 'none',
    borderRadius: '8px',
    margin: 0
  },
  pageItem: {
    margin: '0 2px'
  },
  pageLink: {
    position: 'relative',
    display: 'block',
    padding: '0.5rem 0.75rem',
    marginLeft: '-1px',
    lineHeight: 1.25,
    color: colors.primary,
    backgroundColor: 'var(--surface)',
    border: `1px solid ${colors.border}`,
    cursor: 'pointer',
    borderRadius: '6px'
  },
  disabled: {
    color: colors.textMuted,
    pointerEvents: 'none',
    cursor: 'auto',
    backgroundColor: 'var(--surface)',
    borderColor: colors.border,
    opacity: 0.65
  },
  active: {
    zIndex: 1,
    color: '#fff',
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  floatRight: {
    float: 'right'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1050, // Base modal z-index
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    outline: 0,
    display: 'block',
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)'
  },
  modalSecondary: {
    zIndex: 1060 // Higher z-index for nested modals
  },
  modalDialog: {
    position: 'relative',
    width: 'auto',
    margin: '0.5rem',
    pointerEvents: 'none',
    maxWidth: '600px',
    margin: '1.75rem auto'
  },
  modalContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    pointerEvents: 'auto',
    backgroundColor: 'var(--surface)',
    backgroundClip: 'padding-box',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    outline: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px'
  },
  modalHeaderPrimary: {
    backgroundColor: colors.primary,
    color: '#fff'
  },
  modalTitle: {
    marginBottom: 0,
    lineHeight: 1.5,
    fontSize: '1.25rem',
    fontWeight: 600
  },
  modalBody: {
    position: 'relative',
    flex: '1 1 auto',
    padding: '1.5rem'
  },
  close: {
    float: 'right',
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1,
    color: '#000',
    textShadow: '0 1px 0 #fff',
    opacity: '.5',
    padding: 0,
    backgroundColor: 'transparent',
    border: 0,
    appearance: 'none',
    cursor: 'pointer'
  },
  closeInModal: {
    color: '#fff',
    textShadow: 'none'
  },
  alert: {
    position: 'relative',
    padding: '0.75rem 1.25rem',
    marginBottom: '1rem',
    border: '1px solid transparent',
    borderRadius: '8px'
  },
  alertDanger: {
    color: '#7f1d1d',
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca'
  },
  textDanger: {
    color: colors.danger
  },
  spinAnimation: {
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' }
    },
    animation: 'spin 1s linear infinite'
  }
};

// Custom Dialog Box Component with better z-index handling
const DialogBox = ({ children, onClose, title, isSecondary = false }) => {
  return (
    <div style={{
      ...styles.modal,
      ...(isSecondary ? styles.modalSecondary : {})
    }}>
      <div style={styles.modalDialog}>
        <div style={styles.modalContent}>
          <div style={{ ...styles.modalHeader, ...styles.modalHeaderPrimary }}>
            <h4 style={styles.modalTitle}>{title}</h4>
            <button type="button" style={{ ...styles.close, ...styles.closeInModal }} onClick={onClose}>
              <Icon name="faTimes" />
            </button>
          </div>
          <div style={styles.modalBody}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConnectedDevicesPage = () => {
  const { getUrlByOperation } = useApiUrls();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Calculate current page items

  const navigate = useNavigate();
  const [showRowJsonPopup, setShowRowJsonPopup] = useState(false);
  const [showTypePopup, setShowTypePopup] = useState(false);
  const [rowJson, setRowJson] = useState("");
  const [packageJson, setPackageJson] = useState("");
  const [jsonType, setJsonType] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [finalJson, setFinalJson] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activePopupSource, setActivePopupSource] = useState("");
  const [jsonTypes, setJsonTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for Serial No Modal
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loadingDeviceInfo, setLoadingDeviceInfo] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [editableJson, setEditableJson] = useState("");

  // API data state
  const [devices, setDevices] = useState([]);
  const [fetchingDevices, setFetchingDevices] = useState(true);
  const [deviceError, setDeviceError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return devices.slice(startIndex, endIndex);
  };
  const deviceToSend = selectedDevice || getCurrentItems()[0]; // fallback

  const authToken = getCookie("token");
  const userid = getCookie("userid");
  // Fetch connected devices from API
  const fetchConnectedDevices = async () => {
    setFetchingDevices(true);
    setDeviceError(null);

    try {
      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/Dashboard/ConnectedList',
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': authToken
          }
        }
      );

      if (response.data && response.data.isresponse) {
        const deviceList = response.data.list || [];
        setDevices(deviceList);
        setTotalPages(Math.ceil(deviceList.length / itemsPerPage));
      } else {
        setDeviceError(response.data?.errormsg || "Failed to fetch connected devices");
      }
    } catch (err) {
      console.error("Error fetching connected devices:", err);
      setDeviceError(err.message || "An error occurred while fetching devices");
    } finally {
      setFetchingDevices(false);
    }
  };

  const handleAddNewType = async () => {
    setShowAddTypeModal(true);
  };

  const handleSendFinalJson = async () => {
    try {
      // Validate JSON format - but allow sending empty or invalid JSON as string
      let jsonToSend = null;
      let jsonToDisplay = editableJson.trim();

      // Try to parse JSON, but don't fail if it's not valid JSON
      try {
        if (jsonToDisplay) {
          jsonToSend = JSON.parse(jsonToDisplay);
        }
      } catch (parseError) {
        // If JSON is invalid, we'll still send it as a string in msg field
        console.warn("JSON parsing warning (will send as string):", parseError.message);
        jsonToSend = null; // Will send editableJson as-is in msg field
      }

      // Get selected device data
      const device = selectedDevice;

      if (!device) {
        alert("No device selected.");
        return;
      }

      // Prepare payload - device info goes in payload root, user's JSON goes in msg as-is
      const payload = {
        merchantname: device.merchantname || device.merchantid || "",
        posname: device.posname || "",
        actionname: jsonType || "Clean",
        id: device.id || device.userid || 0,
        withaction: true,
        msg: jsonToDisplay || "" // Send user's JSON exactly as entered, empty string if nothing
      };

      console.log("Sending payload:", payload);

      // Send request
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SendToDevice/General',
        payload,
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': authToken,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      // Handle response - accept any response, even empty
      if (response && response.data) {
        if (response.data.isresponse || response.status === 200) {
          alert('Data sent successfully!');
          setShowTypePopup(false);
        } else {
          alert('Data sent with status: ' + (response.data.responsestatus || 'OK'));
          setShowTypePopup(false);
        }
      } else {
        alert('Data sent successfully (no response data)');
        setShowTypePopup(false);
      }
    } catch (err) {
      console.error("Error sending data:", err.message);
      // Even on error, show that data was attempted to be sent
      alert('Data sent but response error: ' + (err.message || 'Unknown error'));
    }
  };


  const handleActionChange = async (actionName) => {
    setJsonType(actionName);
    if (!actionName) {
      setEditableJson("");
      return;
    }

    const selectedObj = jsonTypes.find(act => act.value === actionName);
    if (!selectedObj) return;

    setEditableJson("Loading configuration JSON...");

    const getUrl = getUrlByOperation("ActionOperationGet");
    if (!getUrl) {
      setEditableJson("");
      return;
    }

    try {
      const response = await axios.post(
        getUrl,
        {
          id: selectedObj.id,
          name: selectedObj.value
        },
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": authToken,
            "Content-Type": "application/json-patch+json"
          }
        }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setEditableJson(response.data.data[0].json || "");
      } else {
        setEditableJson("");
      }
    } catch (error) {
      console.error("Error fetching action JSON:", error);
      setEditableJson("");
    }
  };

  const handleSubmitNewType = async () => {
    try {
      setLoading(true);
      setError(null);

      const insertUrl = getUrlByOperation("ActionOperationInsert");
      if (!insertUrl) {
        throw new Error("API configuration is loading, please try again");
      }

      const response = await axios.post(
        insertUrl,
        {
          name: newTypeName
        },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': '1',
            'X-Authorization': authToken,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        // Refresh the types list
        await fetchJsonTypes();
        setShowAddTypeModal(false);
        setNewTypeName("");
        alert("Type added successfully!");
      } else {
        throw new Error(response.data.errormsg || "Failed to add type");
      }
    } catch (err) {
      console.error("Error adding new type:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch JSON types from API
  const fetchJsonTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const getDistinctUrl = getUrlByOperation("ActionOperationGetDistinctName");
      if (!getDistinctUrl) {
        setJsonTypes([]);
        return [];
      }

      const response = await axios.get(
        getDistinctUrl,
        {
          headers: {
            'accept': 'text/plain',
            'UserId': '1',
            'X-Authorization': authToken
          }
        }
      );

      if (response.data.isresponse && response.data.responsestatus === "SUCCESS" && Array.isArray(response.data.data)) {
        setJsonTypes(response.data.data);
        return response.data.data;
      } else {
        setJsonTypes([]);
        if (response.data.errormsg && response.data.errormsg !== "success") {
          setError(response.data.errormsg);
        }
        return [];
      }
    } catch (err) {
      console.error("Error fetching JSON types:", err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // const handleSendJson = async (source, device) => {
  //   setActivePopupSource(source);
  //   setShowRowJsonPopup(false);

  //   // Set the editable JSON to the initial value based on source
  //   const initialJson = source === "row" ? rowJson : packageJson;
  //   setEditableJson(initialJson);

  // };

  // Handle pagination


  const handleSendJson = async (jsonText) => {
    try {
      // Validate JSON format first
      let jsonToSend;
      try {
        jsonToSend = JSON.parse(jsonText);
      } catch (parseError) {
        alert('Invalid JSON format. Please check your JSON syntax.');
        return;
      }

      // Get selected device data
      const device = selectedDevice;

      if (!device) {
        alert("No device selected.");
        return;
      }

      // Prepare payload - device info goes in payload root, user's JSON goes in msg as-is
      const payload = {
        merchantname: device.merchantname || device.merchantid || "",
        posname: device.posname || "",
        actionname: "Clean", // You can make this dynamic if needed
        id: device.id || device.userid || 0,
        withaction: false, // Set to false for row JSON
        msg: jsonText // Send user's JSON exactly as entered, without modification
      };

      console.log("Sending payload:", payload);

      // Send request
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SendToDevice/General',
        payload,
        {
          headers: {
            accept: 'text/plain',
            UserId: userid,
            'X-Authorization': authToken,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse) {
        alert('JSON sent successfully!');
        setShowRowJsonPopup(false);
      } else {
        alert(`Error: ${response.data?.errormsg || 'Failed to send JSON'}`);
      }
    } catch (err) {
      console.error("Error sending JSON:", err);
      alert(`Error sending JSON: ${err.response?.data?.errormsg || err.message}`);
    }
  };

  // Updated Row JSON popup button click handler
  const handleRowJsonSend = () => {
    setActivePopupSource("row");
    handleSendJson(rowJson);
  };

  const handleSerialClick = async (serialno) => {
    if (!serialno) return;

    setShowSerialModal(true);
    setLoadingDeviceInfo(true);
    setDeviceInfo(null);

    const userid = getCookie("userid");
    const token = getCookie("token");

    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/DeviceInfoOperation/Search',
        {
          serialno: serialno,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string"
        },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse && response.data.data) {
        setDeviceInfo(response.data.data);
      } else {
        setDeviceInfo([]);
        console.error("Invalid response format or error from API");
      }
    } catch (error) {
      console.error("Error fetching device info:", error);
      setDeviceInfo([]);
    } finally {
      setLoadingDeviceInfo(false);
    }
  };

  const closeSerialModal = () => {
    setShowSerialModal(false);
    setDeviceInfo(null);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };


  const handleSendJsonPackage = (device) => {
    setSelectedDevice(device); // Set selected device
    setActivePopupSource("package");

    // Don't generate initial JSON, just set empty values
    setPackageJson("");
    setEditableJson(""); // Start with empty editable JSON
    setShowTypePopup(true);
  };
  const handleRowJsonButtonClick = (device) => {
    setSelectedDevice(device);
    setActivePopupSource("row");
    setRowJson(""); // Start with empty JSON
    setEditableJson(""); // Start with empty editable JSON
    setShowRowJsonPopup(true);
  };

  const exampleJsons = {
    row: `{
  "action": "display_message",
  "message": "Hello World!",
  "duration": 5000,
  "priority": "high"
}`,
    package: `{
  "package_name": "my_package",
  "version": "1.0.0"
}`
  };

  // useEffect for fetching JSON types when popup opens
  useEffect(() => {
    if (showTypePopup) {
      fetchJsonTypes();
    }
  }, [showTypePopup]);

  // Update editable JSON when the popup source changes
  useEffect(() => {
    if (showTypePopup) {
      const initialJson = activePopupSource === "row" ? rowJson : packageJson;
      setEditableJson(initialJson);
    }
  }, [showTypePopup, activePopupSource, rowJson, packageJson]);

  // Fetch devices on component mount
  useEffect(() => {
    fetchConnectedDevices();
  }, []);

  // Fetch JSON types when getUrlByOperation hook resolves or updates
  useEffect(() => {
    fetchJsonTypes();
  }, [getUrlByOperation]);

  return (
    <div>
      <Menu />

      {/* Content Wrapper */}
      <div className="content-wrapper">
        {/* Content Header (Page header) */}
        <section style={styles.contentHeader}>
          <div style={{ ...styles.containerFluid, width: '100%' }}>
            <div style={{ ...styles.row, ...styles.mb2 }}>
              {/* This row spans full width, with left and right sections */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>

                {/* Left Side - Label */}
                <h1 style={{  fontWeight: 600, margin: 0 }}>
                  Connected Devices
                </h1>

                {/* Right Side - Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnInfo }}
                    onClick={() => navigate('/Send-Row-Json')}
                  >
                    <span style={styles.mr1}></span>
                    Send Row JSON
                  </button>

                  <button
                    style={{ ...styles.btn, ...styles.btnSm, ...styles.btnPrimary }}
                    onClick={() => navigate('/Send-json-Package')}
                  >
                    <span style={styles.mr1}></span>
                    Send JSON Package
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Main content */}
        <section style={{ padding: '20px 0' }}>
          <div style={styles.containerFluid}>
            <div style={styles.row}>
              <div style={styles.col12}>
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <h3 style={styles.cardTitle}>
                      <span style={styles.mr2}><Icon name="faTable" /></span>
                      Connected Devices List
                    </h3>
                  </div>
                  {/* Card Body */}
                  <div style={styles.cardBody}>
                    {fetchingDevices ? (
                      <div style={{ ...styles.textCenter, ...styles.p4 }}>
                        <span style={styles.spinAnimation}><Icon name="faSpinner" /></span>
                        <p style={{ ...styles.mt2, color: colors.textMuted }}>Loading devices...</p>
                      </div>
                    ) : deviceError ? (
                      <div style={styles.alertDanger}>
                        <p>{deviceError}</p>
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ ...styles.table, ...styles.tableBordered, ...styles.tableHover }}>
                          <thead>
                            <tr style={{ backgroundColor: colors.lighter }}>
                              <th style={styles.th}>Userid</th>
                              <th style={styles.th}>Posname</th>
                              <th style={styles.th}>Merchantid</th>
                              <th style={styles.th}>Key</th>
                              <th style={styles.th}>Active</th>
                              <th style={styles.th}>Serialno</th>
                              <th style={styles.th}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getCurrentItems().length > 0 ? (
                              getCurrentItems().map((device, index) => (
                                <tr key={index} style={{
                                  transition: 'background-color 0.15s ease',
                                  ':hover': { backgroundColor: colors.lighter }
                                }}>
                                  <td style={styles.td}>{device.id}</td>
                                  <td style={styles.td}>{device.posname}</td>
                                  <td style={styles.td}>{device.merchantid}</td>
                                  <td style={styles.td}>{device.key}</td>
                                  <td style={styles.td}>
                                    <span style={{
                                      ...styles.badge,
                                      ...(device.active === "True" ? styles.badgeSuccess : styles.badgeDanger)
                                    }}>
                                      {device.active}
                                    </span>
                                  </td>
                                  <td style={styles.td}>
                                    {device.serialno ? (
                                      <button
                                        style={{ ...styles.btn, ...styles.btnSm, ...styles.btnInfo, backgroundColor: 'transparent', color: colors.info, borderColor: colors.info }}
                                        onClick={() => handleSerialClick(device.serialno)}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = colors.info;
                                          e.target.style.color = '#fff';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.color = colors.info;
                                        }}
                                      >
                                        {device.serialno}
                                      </button>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td style={styles.td}>
                                    <div style={styles.btnGroup}>
                                      <button
                                        style={{ ...styles.btn, ...styles.btnSm, ...styles.btnInfo }}
                                        onClick={() => handleRowJsonButtonClick(device)} // Updated function call
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = colors.infoHover;
                                          e.target.style.borderColor = colors.infoHover;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = colors.info;
                                          e.target.style.borderColor = colors.info;
                                        }}
                                      >
                                        <span style={styles.mr1}></span>
                                        Send Row JSON
                                      </button>


                                      <button
                                        style={{ ...styles.btn, ...styles.btnSm, ...styles.btnPrimary }}
                                        onClick={() => handleSendJsonPackage(device)} // This function is already updated above
                                      >
                                        <span style={styles.mr1}></span>
                                        Send JSON Package
                                      </button>
                                      <select
                                        style={{
                                          ...styles.formControl,
                                          ...styles.formControlSm,
                                          ...styles.ml1,
                                          width: 'auto',
                                          cursor: 'pointer'
                                        }}
                                        onChange={(e) => {
                                          const selectedOption = e.target.value;
                                          const merchantname = device.merchantname || device.merchantid || "";
                                          const posid = device.posname || "";
                                          if (selectedOption === "send_campaign") {
                                            navigate(`/sendcompaign/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_audio") {
                                            navigate(`/sendaudio/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_audio_Package") {
                                            navigate(`/sendaudioPackage/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_catalog") {
                                            navigate(`/send-catelog/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_dqr") {
                                            navigate(`/send-Dqr-image/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_firmware") {
                                            navigate(`/send-firmware/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "send_MQTT_Setting") {
                                            navigate(`/sendMQTTSetting/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption === "sendSerial_noMqtt_Setting") {
                                            navigate(`/sendSerialnoMqttSetting/${merchantname}/${posid}`, {
                                              state: { merchantname, posid }
                                            });
                                          } else if (selectedOption) {
                                            console.log(`Selected option: ${selectedOption}`);
                                          }
                                        }}
                                      >
                                        <option value="">Select Action</option>
                                        <option value="send_campaign">Send Campaign</option>
                                        <option value="send_audio">Send Audio</option>
                                        <option value="send_audio_Package">Send Audio Package</option>
                                        <option value="send_catalog">Send Catalogue</option>
                                        <option value="send_dqr">Send DQR Images</option>
                                        <option value="send_firmware">Send Firmware</option>
                                        <option value="send_MQTT_Setting">Send Json</option>
                                        <option value="sendSerial_noMqtt_Setting">Send MQTTSetting</option>

                                      </select>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" style={{ ...styles.textCenter, color: colors.textMuted }}>
                                  No connected devices found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  {/* Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    totalItems={devices.length}
                    showPageNumbers={true}
                    showItemsPerPageSelector={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Row JSON Popup */}
      {showRowJsonPopup && (
        <DialogBox onClose={() => setShowRowJsonPopup(false)} title="Send Row JSON">
          <div style={styles.formGroup}>
            <label style={{ fontWeight: 500, color: colors.dark }}>JSON Data</label>
            <textarea
              rows={8}
              style={{ ...styles.formControl, height: 'auto', fontFamily: 'monospace' }}
              value={rowJson}
              onChange={(e) => setRowJson(e.target.value)}
              placeholder={exampleJsons.row}
            ></textarea>
            <small style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              Enter your JSON data above. See placeholder for example format.
            </small>
          </div>
          <div style={{ ...styles.dFlex, ...styles.justifyContentEnd }}>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary, ...styles.mr2 }}
              onClick={handleRowJsonSend}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.primaryHover;
                e.target.style.borderColor = colors.primaryHover;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.primary;
                e.target.style.borderColor = colors.primary;
              }}
            >
              <span style={styles.mr1}></span>
              Send JSON
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnDefault }}
              onClick={() => setShowRowJsonPopup(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.lighter;
                e.target.style.borderColor = colors.secondary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fff';
                e.target.style.borderColor = colors.border;
              }}
            >
              Close
            </button>
          </div>
        </DialogBox>
      )}

      {/* Add New Type Modal - This will appear above the type selection popup */}
      {showAddTypeModal && (
        <DialogBox
          onClose={() => setShowAddTypeModal(false)}
          title="Add New JSON Type"
          isSecondary={true}
        >
          <div style={styles.formGroup}>
            <label style={{ fontWeight: 500, color: colors.dark }}>New Type Name</label>
            <input
              type="text"
              style={styles.formControl}
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Enter new type name"
            />
          </div>
          <div style={{ ...styles.dFlex, ...styles.justifyContentEnd }}>
            <button
              style={{
                ...styles.btn,
                ...styles.btnSuccess,
                ...styles.mr2,
                ...((!newTypeName || loading) ? styles.disabled : {})
              }}
              onClick={handleSubmitNewType}
              disabled={!newTypeName || loading}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = colors.successHover;
                  e.target.style.borderColor = colors.successHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = colors.success;
                  e.target.style.borderColor = colors.success;
                }
              }}
            >
              {loading ? "Adding..." : "Add Type"}
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnDefault }}
              onClick={() => setShowAddTypeModal(false)}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = colors.lighter;
                  e.target.style.borderColor = colors.secondary;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = '#fff';
                  e.target.style.borderColor = colors.border;
                }
              }}
            >
              Cancel
            </button>
          </div>
          {error && <div style={{ ...styles.alert, ...styles.alertDanger, ...styles.mt2 }}>{error}</div>}
        </DialogBox>
      )}

      {/* JSON Type Selection Popup */}
      {showTypePopup && (
        <DialogBox onClose={() => setShowTypePopup(false)} title="Select JSON Type">
          <div style={styles.formGroup}>
            <div style={{ ...styles.dFlex, ...styles.alignItemsCenter }}>
              <div style={styles.flex1}>
                <label style={{ fontWeight: 500, color: colors.dark }}>JSON Type</label>
                <select
                  value={jsonType}
                  onChange={(e) => handleActionChange(e.target.value)}
                  style={styles.formControl}
                >
                  <option value="">Select type</option>
                  {loading && <option value="" disabled>Loading types...</option>}
                  {!loading && jsonTypes.length > 0 && jsonTypes.map((item, index) => (
                    <option key={item.id} value={item.value}>{item.value}</option>
                  ))}
                  {!loading && jsonTypes.length === 0 && !error && <option value="" disabled>No types available</option>}
                </select>
                {error && <div style={{ ...styles.textDanger, ...styles.mt1 }}>Error loading types: {error}</div>}
              </div>
              <button
                style={{
                  ...styles.btn,
                  ...styles.btnPrimary,
                  ...styles.ml2,
                  marginTop: '24px',
                  width: '44px',
                  height: '44px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={handleAddNewType}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryHover;
                  e.target.style.borderColor = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.borderColor = colors.primary;
                }}
              >
                <Icon name="faPlus" />
              </button>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={{ fontWeight: 500, color: colors.dark }}>JSON Data (Editable)</label>
            <textarea
              rows={10}
              style={{
                ...styles.formControl,
                height: 'auto',
                fontFamily: 'monospace',
                backgroundColor: 'var(--surface)',
                border: `2px solid ${colors.primary}`,
                resize: 'vertical'
              }}
              value={editableJson}
              onChange={(e) => setEditableJson(e.target.value)}
              placeholder={activePopupSource === "package" ? exampleJsons.package : exampleJsons.row}
            ></textarea>
            <small style={{ color: colors.textMuted, fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              Enter your JSON data above. See placeholder for example format.
            </small>
          </div>
          <div style={{ ...styles.dFlex, ...styles.justifyContentEnd }}>
            <button
              style={{
                ...styles.btn,
                ...styles.btnSuccess,
                ...styles.mr2,
                ...(!jsonType ? styles.disabled : {})
              }}
              onClick={handleSendFinalJson}
              disabled={!jsonType}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = colors.successHover;
                  e.target.style.borderColor = colors.successHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.backgroundColor = colors.success;
                  e.target.style.borderColor = colors.success;
                }
              }}
            >
              <span style={styles.mr1}></span>
              Send JSON
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnDefault }}
              onClick={() => setShowTypePopup(false)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.lighter;
                e.target.style.borderColor = colors.secondary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fff';
                e.target.style.borderColor = colors.border;
              }}
            >
              Close
            </button>
          </div>
        </DialogBox>
      )}

      {/* Serial No Modal */}
      {showSerialModal && (
        <DialogBox onClose={closeSerialModal} title="Device Information">
          {loadingDeviceInfo ? (
            <div style={{ ...styles.textCenter, ...styles.p4 }}>
              <span style={styles.spinAnimation}><Icon name="faSpinner" /></span>
              <p style={{ ...styles.mt2, color: colors.textMuted }}>Loading device information...</p>
            </div>
          ) : deviceInfo && deviceInfo.length > 0 ? (
            <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
              <table style={{ ...styles.table, ...styles.tableBordered, ...styles.tableHover }}>
                <thead>
                  <tr style={{ backgroundColor: colors.lighter }}>
                    <th style={styles.th}>Property</th>
                    <th style={styles.th}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceInfo.map((device, index) => (
                    <React.Fragment key={index}>
                      <tr><td style={styles.td}><strong>Serial No</strong></td><td style={styles.td}>{device.serialno}</td></tr>
                      <tr><td style={styles.td}><strong>Device Type</strong></td><td style={styles.td}>{device.devicetype}</td></tr>
                      <tr><td style={styles.td}><strong>Model No</strong></td><td style={styles.td}>{device.devicemodelno}</td></tr>
                      <tr><td style={styles.td}><strong>Hardware</strong></td><td style={styles.td}>{device.devicehardware}</td></tr>
                      <tr><td style={styles.td}><strong>Software Version</strong></td><td style={styles.td}>{device.devicesoftwareversion}</td></tr>
                      <tr><td style={styles.td}><strong>Resolution</strong></td><td style={styles.td}>{device.width} x {device.height}</td></tr>
                      <tr><td style={styles.td}><strong>Active</strong></td><td style={styles.td}>{device.deviceactive ? "Yes" : "No"}</td></tr>
                      <tr><td style={styles.td}><strong>Created Date</strong></td><td style={styles.td}>{device.createddate}</td></tr>
                      <tr><td style={styles.td}><strong>Last Modified</strong></td><td style={styles.td}>{device.lastmodifieddate}</td></tr>
                      <tr><td style={styles.td}><strong>Orientation</strong></td><td style={styles.td}>{device.orientation || "-"}</td></tr>
                      <tr><td style={styles.td}><strong>Firmware</strong></td><td style={styles.td}>{device.firmware || "-"}</td></tr>
                      <tr><td style={styles.td}><strong>POS Name</strong></td><td style={styles.td}>{device.posname || "-"}</td></tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ ...styles.alert, ...styles.alertDanger }}>
              No device information found.
            </div>
          )}
          <div style={{ ...styles.dFlex, ...styles.justifyContentEnd, marginTop: '20px' }}>
            <button
              style={{ ...styles.btn, ...styles.btnDefault }}
              onClick={closeSerialModal}
            >
              Close
            </button>
          </div>
        </DialogBox>
      )}

    </div>
  );
};

export default ConnectedDevicesPage;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}